<template>
  <div class="dash-home">
    <div class="kpi-row">
      <div v-for="kpi in kpis" :key="kpi.label" class="kpi-card" :class="{ 'is-empty': kpi.empty }">
        <div class="kpi-icon" :class="kpi.tone">
          <i :class="['pi', kpi.icon]" aria-hidden="true" />
        </div>
        <div class="kpi-body">
          <div class="kpi-label">{{ kpi.label }}</div>
          <div class="kpi-value">{{ kpi.value }}</div>
          <div v-if="kpi.hint" class="kpi-hint">{{ kpi.hint }}</div>
        </div>
      </div>
    </div>

    <Card class="tw-mb-4 chart-card">
      <template #title>
        <div class="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-2">
          <div>
            <div class="chart-kicker">{{ hasWarehouseData ? 'Listings in warehouse' : 'Records synced' }}</div>
            <div class="chart-total">{{ formatNumber(hasWarehouseData ? warehouseListingTotal : totalRecordsSynced) }}</div>
          </div>
          <div class="tw-text-sm tw-text-muted">
            <span v-if="hasWarehouseData">Live Brokurz inventory</span>
            <span v-else>Last {{ days }} days · job history</span>
          </div>
        </div>
      </template>
      <template #content>
        <div v-if="hasWarehouseData" class="warehouse-panel">
          <div v-for="w in warehouseFeeds" :key="w.sourceName + (w.feedSlug || '')" class="warehouse-row">
            <div>
              <div class="tw-font-semibold">{{ w.feedDisplayName || w.feedSlug || w.sourceName }}</div>
              <div class="tw-text-sm tw-text-muted">
                source <code>{{ w.sourceName }}</code>
                <span v-if="w.feedStatus"> · {{ w.feedStatus }}</span>
              </div>
            </div>
            <div class="tw-text-right">
              <div class="tw-font-semibold tw-text-lg">{{ formatNumber(w.listingCount) }}</div>
              <div class="tw-text-sm tw-text-muted">
                <display-datetime v-if="w.lastSuccessfulSyncAt" :datetime="w.lastSuccessfulSyncAt" />
                <em v-else>Synced via CLI / Config</em>
              </div>
            </div>
            <Button
              label="Open"
              icon="pi pi-arrow-right"
              iconPos="right"
              class="p-button-text p-button-sm"
              @click="$router.push(`/sources/${w.sourceName}`)"
            />
          </div>
        </div>
        <div v-else-if="!hasChartData" class="empty-chart">
          <div class="empty-chart-canvas" aria-hidden="true">
            <svg viewBox="0 0 640 220" preserveAspectRatio="none" class="empty-chart-svg">
              <line x1="40" y1="20" x2="40" y2="180" stroke="#e4e4e7" stroke-width="1" />
              <line x1="40" y1="180" x2="620" y2="180" stroke="#e4e4e7" stroke-width="1" />
              <line v-for="y in [50, 90, 130]" :key="y" :x1="40" :y1="y" x2="620" :y2="y" stroke="#f4f4f5" stroke-width="1" />
              <path
                d="M40 170 C 120 165, 180 155, 240 150 S 360 140, 420 145 S 540 160, 620 155"
                fill="none"
                stroke="#d4d4d8"
                stroke-width="2"
                stroke-dasharray="6 6"
              />
              <path
                d="M40 180 L40 170 C 120 165, 180 155, 240 150 S 360 140, 420 145 S 540 160, 620 155 L620 180 Z"
                fill="rgba(24,24,27,0.03)"
              />
            </svg>
            <div class="empty-chart-overlay">
              <div class="empty-icon"><i class="pi pi-chart-line" /></div>
              <div class="empty-title">No sync volume yet</div>
              <p class="empty-copy">
                When feeds sync, daily record counts will appear here for the last {{ days }} days.
              </p>
              <div class="empty-actions">
                <Button label="Open Config" icon="pi pi-cog" class="p-button-sm" @click="$router.push('/config')" />
                <Button label="View Jobs" icon="pi pi-briefcase" class="p-button-outlined p-button-sm" @click="$router.push('/jobs')" />
                <Button label="Docs" icon="pi pi-book" class="p-button-text p-button-sm" @click="$router.push('/docs')" />
              </div>
            </div>
          </div>
        </div>
        <Chart
          v-else
          type="line"
          :data="chartData"
          :options="chartOptions"
          :height="280"
          class="dash-chart"
        />
      </template>
    </Card>

    <Card>
      <template #title>Feeds</template>
      <template #subtitle>Latest sync / purge / reconcile per MLS source</template>
      <template #content>
        <div v-if="!feedRows.length" class="empty-panel">
          <div class="empty-icon"><i class="pi pi-database" /></div>
          <div class="empty-title">No feed activity yet</div>
          <p class="empty-copy">
            Connect an MLS in Config, then run a sync from a source page or schedule. History will show up here.
          </p>
          <div class="empty-actions">
            <Button label="Add MLS feed" icon="pi pi-plus" class="p-button-sm" @click="$router.push('/config')" />
            <Button label="Schedules" icon="pi pi-calendar" class="p-button-outlined p-button-sm" @click="$router.push('/cron')" />
            <Button label="Docs" icon="pi pi-book" class="p-button-text p-button-sm" @click="$router.push('/docs')" />
          </div>
        </div>
        <div v-else class="feeds-table">
          <div class="feeds-head">
            <span>Source</span>
            <span>Last sync</span>
            <span>Status</span>
            <span>Records</span>
            <span />
          </div>
          <div v-for="row in feedRows" :key="row.name" class="feeds-row">
            <span class="tw-font-semibold">{{ row.name }}</span>
            <span>
              <display-datetime v-if="row.lastSyncAt" :datetime="row.lastSyncAt" />
              <em v-else class="tw-text-muted">Never</em>
            </span>
            <span>
              <StatusIcon
                v-if="row.lastSyncResult"
                :ok="row.lastSyncResult === 'success'"
                :title="row.lastSyncResult"
              />
              <span v-else class="tw-text-muted">—</span>
            </span>
            <span>{{ formatNumber(row.records) }}</span>
            <Button
              label="Open"
              icon="pi pi-arrow-right"
              iconPos="right"
              class="p-button-text p-button-sm"
              @click="$router.push(`/sources/${row.name}`)"
            />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import groupBy from 'lodash/groupBy'
import sumBy from 'lodash/sumBy'
import union from 'lodash/union'
import moment from 'moment'
import { convertBatchIdToTimestamp } from '../../lib/sync/utils/datetime'
import StatusIcon from './StatusIcon'

const DAYS = 14

function recordsFromSync(sync) {
  if (!sync || !sync.resources) return 0
  return sumBy(sync.resources, r => sumBy(r.destinations || [], d => d.num_records_synced || 0))
}

export default {
  components: { StatusIcon },
  inject: {
    jobsProvider: { default: null },
  },
  props: {
    stats: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      days: DAYS,
      chartOptions: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: '#18181b',
            titleColor: '#fff',
            bodyColor: '#e4e4e7',
            padding: 10,
            cornerRadius: 6,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#a1a1aa', maxRotation: 0 },
            border: { display: false },
          },
          y: {
            beginAtZero: true,
            grid: { color: '#f4f4f5' },
            ticks: {
              color: '#a1a1aa',
              callback(v) {
                if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`
                if (v >= 1000) return `${(v / 1000).toFixed(0)}k`
                return v
              },
            },
            border: { display: false },
          },
        },
        elements: {
          line: { tension: 0.35, borderWidth: 2 },
          point: { radius: 0, hoverRadius: 4 },
        },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
      },
    }
  },
  methods: {
    formatNumber(n) {
      return Number(n || 0).toLocaleString()
    },
    convertBatchIdToTimestamp,
  },
  computed: {
    syncList() {
      return (this.stats && this.stats.sync) || []
    },
    warehouseFeeds() {
      return (this.stats && this.stats.warehouseFeeds) || []
    },
    hasWarehouseData() {
      return this.warehouseFeeds.some(w => (w.listingCount || 0) > 0)
    },
    warehouseListingTotal() {
      return sumBy(this.warehouseFeeds, w => w.listingCount || 0)
    },
    sourceNames() {
      const sync = groupBy(this.syncList, x => x.name)
      const purge = groupBy((this.stats && this.stats.purge) || [], x => x.name)
      const reconcile = groupBy((this.stats && this.stats.reconcile) || [], x => x.name)
      const fromHistory = union(Object.keys(sync), Object.keys(purge), Object.keys(reconcile))
      const fromWarehouse = this.warehouseFeeds.map(w => w.sourceName)
      return union(fromHistory, fromWarehouse).sort()
    },
    totalRecordsSynced() {
      return sumBy(this.syncList, recordsFromSync)
    },
    successRate() {
      if (!this.syncList.length) return null
      const ok = this.syncList.filter(s => s.result === 'success').length
      return Math.round((ok / this.syncList.length) * 100)
    },
    runningJobs() {
      if (!this.jobsProvider || this.jobsProvider.loading || this.jobsProvider.error) return null
      return this.jobsProvider.runningJobs.length
    },
    kpis() {
      const noHistory = !this.syncList.length
      const wh = this.hasWarehouseData
      return [
        {
          label: 'Sources',
          value: this.sourceNames.length || (wh ? this.warehouseFeeds.length : 0),
          hint: wh ? 'Warehouse-connected feeds' : (this.sourceNames.length ? 'With recorded activity' : 'No activity recorded yet'),
          icon: 'pi-database',
          tone: 'tone-ink',
          empty: !(this.sourceNames.length || wh),
        },
        {
          label: wh ? 'Warehouse listings' : 'Sync success',
          value: wh
            ? this.formatNumber(this.warehouseListingTotal)
            : (this.successRate === null ? '—' : `${this.successRate}%`),
          hint: wh
            ? 'Live in Brokurz listings warehouse'
            : (noHistory ? 'Waiting for first sync' : `${this.syncList.length} sync jobs recorded`),
          icon: wh ? 'pi-home' : 'pi-check-circle',
          tone: wh ? 'tone-ok' : (this.successRate !== null && this.successRate < 90 ? 'tone-warn' : 'tone-ok'),
          empty: !(wh || !noHistory),
        },
        {
          label: wh ? 'Job history records' : 'Records synced',
          value: this.formatNumber(this.totalRecordsSynced),
          hint: wh
            ? (noHistory ? 'CLI syncs don’t write job history yet' : 'Across recorded sync jobs')
            : (noHistory ? 'Will update after syncs land' : 'Across all sync destinations'),
          icon: 'pi-chart-line',
          tone: 'tone-ink',
          empty: noHistory && !wh,
        },
        {
          label: 'Running jobs',
          value: this.runningJobs === null ? '…' : this.runningJobs,
          hint: this.runningJobs ? 'Active right now' : 'Queue idle',
          icon: 'pi-play',
          tone: 'tone-ink',
          empty: false,
        },
      ]
    },
    dayBuckets() {
      const start = moment().startOf('day').subtract(DAYS - 1, 'days')
      const labels = []
      const values = []
      const map = {}
      for (let i = 0; i < DAYS; i++) {
        const day = start.clone().add(i, 'days')
        const key = day.format('YYYY-MM-DD')
        labels.push(day.format('MMM D'))
        map[key] = 0
      }
      this.syncList.forEach(sync => {
        const ts = convertBatchIdToTimestamp(sync.batch_id)
        if (!ts) return
        const key = moment(ts).format('YYYY-MM-DD')
        if (Object.prototype.hasOwnProperty.call(map, key)) {
          map[key] += recordsFromSync(sync)
        }
      })
      Object.keys(map).sort().forEach(k => values.push(map[k]))
      return { labels, values }
    },
    hasChartData() {
      return this.dayBuckets.values.some(v => v > 0)
    },
    chartData() {
      const { labels, values } = this.dayBuckets
      return {
        labels,
        datasets: [
          {
            label: 'Records synced',
            data: values,
            borderColor: '#18181b',
            backgroundColor: 'rgba(24, 24, 27, 0.08)',
            fill: true,
          },
        ],
      }
    },
    feedRows() {
      const byName = groupBy(this.syncList, x => x.name)
      const byWarehouse = groupBy(this.warehouseFeeds, x => x.sourceName)
      return this.sourceNames.map(name => {
        const list = byName[name] || []
        const latest = list[0]
        const wh = (byWarehouse[name] || [])[0]
        return {
          name,
          lastSyncAt: latest
            ? convertBatchIdToTimestamp(latest.batch_id)
            : (wh && wh.lastSuccessfulSyncAt) || null,
          lastSyncResult: latest ? latest.result : (wh && wh.listingCount > 0 ? 'success' : null),
          records: wh && wh.listingCount > 0
            ? wh.listingCount
            : sumBy(list, recordsFromSync),
        }
      })
    },
  },
}
</script>

<style scoped>
.warehouse-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.warehouse-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1rem;
  align-items: center;
  padding: 0.85rem 0.25rem;
  border-bottom: 1px solid #f4f4f5;
}

.warehouse-row:last-child {
  border-bottom: 0;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.kpi-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.kpi-card.is-empty .kpi-value {
  color: #a1a1aa;
}

.kpi-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f4f4f5;
  color: #18181b;
}

.kpi-icon.tone-ok {
  background: #ecfdf5;
  color: #166534;
}

.kpi-icon.tone-warn {
  background: #fef2f2;
  color: #b91c1c;
}

.kpi-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-top: 0.15rem;
  color: #18181b;
}

.kpi-hint {
  font-size: 0.75rem;
  color: #a1a1aa;
  margin-top: 0.2rem;
}

.chart-kicker {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.chart-total {
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #18181b;
}

.dash-chart {
  max-height: 280px;
  height: 280px;
  overflow: hidden;
}

.dash-chart ::v-deep canvas {
  max-height: 280px !important;
}

.empty-chart-canvas {
  position: relative;
  min-height: 280px;
  border: 1px dashed #e4e4e7;
  border-radius: 8px;
  background: #fafafa;
  overflow: hidden;
}

.empty-chart-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.9;
}

.empty-chart-overlay {
  position: relative;
  z-index: 1;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
}

.empty-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.25rem 1.25rem;
  border: 1px dashed #e4e4e7;
  border-radius: 8px;
  background: #fafafa;
}

.empty-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e4e4e7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #52525b;
  margin-bottom: 0.85rem;
  font-size: 1.1rem;
}

.empty-title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #18181b;
}

.empty-copy {
  margin: 0.4rem 0 1rem;
  max-width: 26rem;
  font-size: 0.875rem;
  color: #71717a;
  line-height: 1.45;
}

.empty-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.feeds-table {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.feeds-head,
.feeds-row {
  display: grid;
  grid-template-columns: 1.4fr 1.2fr 0.7fr 0.9fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f4f4f5;
  font-size: 0.875rem;
}

.feeds-head {
  color: #71717a;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom-color: #e4e4e7;
}

@media (max-width: 1000px) {
  .kpi-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .kpi-row {
    grid-template-columns: 1fr;
  }

  .feeds-head {
    display: none;
  }

  .feeds-row {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
